import { DefinitionResource } from '@mode/legacy/data-access-model-js';
import { AppState, DefinitionState, SelectedDefinitionState } from '@mode/legacy/types-app-state';
import { ReducerTree } from '@mode/legacy/util-domain';
import { Switch } from '@mode/shared/util-switch';
import { ClientActionType } from '../../../actions/client.actions';
import { Actions } from '../../../actions/index';
import { ReportRunActionType } from '../../../actions/report-run.actions';
import {
  queryResourceToState,
  queryResultToState,
  queryRunResourceToState,
  reportRunResourceToState,
} from '../../../reducers/client/selected-report.reducers';
import { SelectedDefinitionActionType } from './definition.actions';

export function definitionResourceToState(definition: DefinitionResource): DefinitionState {
  return {
    resource: definition,
    name: definition.name,
    source: definition.source,
    description: definition.description,
    dataSourceId: definition.data_source_id,
    token: definition.token,
    created_at: definition.created_at,
    updated_at: definition.updated_at,
    lastSyncedAt: definition.last_successful_sync_at && new Date(definition.last_successful_sync_at),
  };
}

export const initialSelectedDefinitionState: SelectedDefinitionState = {
  definition: null,
  query: null,
  reportRun: null,
  space: null,
  queryRun: null,
  chartModel: null,
  model: Switch.Models.empty(),
  pollingDefinitionRun: false,
};

export function definitionReducer(
  state = initialSelectedDefinitionState.definition,
  action: Actions
): SelectedDefinitionState['definition'] {
  switch (action.type) {
    case ClientActionType.LoadDefinitionSuccess:
      return definitionResourceToState(action.definitionResource);
    case SelectedDefinitionActionType.UpdateDefinitionSuccess:
      return definitionResourceToState(action.definition);
    case SelectedDefinitionActionType.UpdateDefinitionSource:
      return {
        ...state,
        source: action.source,
      };
  }

  return state;
}

export function queryReducer(
  state = initialSelectedDefinitionState.query,
  action: Actions
): SelectedDefinitionState['query'] {
  switch (action.type) {
    case SelectedDefinitionActionType.LoadDefinitionQuerySuccess:
      return queryResourceToState(action.resource);
  }
  return state;
}

export function reportRunReducer(
  state = initialSelectedDefinitionState.reportRun,
  action: Actions
): SelectedDefinitionState['reportRun'] {
  switch (action.type) {
    case SelectedDefinitionActionType.DefinitionReportRunLoadSuccess:
      return reportRunResourceToState(action.resource);
  }
  return state;
}

export function queryRunReducer(
  state = initialSelectedDefinitionState.queryRun,
  action: Actions
): SelectedDefinitionState['queryRun'] {
  switch (action.type) {
    case SelectedDefinitionActionType.LoadDefinitionQueryRunSuccess:
      // Don't want reloading the query run after a cancel to reset the canceling state
      let runBytes;
      let runContentLength;

      let runQueued = false;
      let cancelQueued = false;
      let localCreatedAt: Date = null;

      if (state) {
        runQueued = state.runQueued;
        cancelQueued = state.cancelQueued;
        localCreatedAt = state.localCreatedAt;

        runBytes = state.bytes;
        runContentLength = state.content_length;
      }

      return {
        ...queryRunResourceToState(action.resource, cancelQueued, runQueued, runBytes, runContentLength),
        localCreatedAt,
      };
    case SelectedDefinitionActionType.CancelDefinitionQueryRunSuccess:
      return queryRunResourceToState(action.resource, false, false, undefined, undefined);
    case SelectedDefinitionActionType.ExecuteDefinitionRun:
      return {
        ...state,
        state: 'running',
        running: true,
        limit: action.limit,
        cancelQueued: false,
        localCreatedAt: new Date(),
      };
    case SelectedDefinitionActionType.CancelDefinitionQueryRun:
      return {
        ...state,
        running: false,
        cancelQueued: true,
        runQueued: false,
      };
    case SelectedDefinitionActionType.CancelDefinitionQueryRunFailed:
      return {
        ...state,
        running: true,
        cancelQueued: false,
      };
    case SelectedDefinitionActionType.LoadDefinitionQueryResultSuccess:
    case SelectedDefinitionActionType.LoadDefinitionQueryResultFailure:
      return {
        ...state,
        runQueued: false,
        cancelQueued: false,
      };
  }

  return state;
}

export function modelReducer(
  state = initialSelectedDefinitionState.model,
  action: Actions
): SelectedDefinitionState['model'] {
  switch (action.type) {
    case ReportRunActionType.LoadQueryRunView: {
      const { view } = action;
      return Switch.Views.merge(state, view);
    }
    default:
      return state;
  }
}

export function chartModelReducer(
  state = initialSelectedDefinitionState.chartModel,
  action: Actions
): SelectedDefinitionState['chartModel'] {
  switch (action.type) {
    case SelectedDefinitionActionType.LoadDefinitionQueryResultSuccess:
      return queryResultToState(
        action.queryResource,
        action.resource,
        action.queryRun,
        action.model,
        action.dataset ? action.dataset.content : []
      );
    case SelectedDefinitionActionType.ExecuteDefinitionRun:
      return null;
  }
  return state;
}

export function pollingReportRunReducer(
  state = initialSelectedDefinitionState.pollingDefinitionRun,
  action: Actions
): SelectedDefinitionState['pollingDefinitionRun'] {
  switch (action.type) {
    case SelectedDefinitionActionType.PollDefinitionRun:
      return true;
    case SelectedDefinitionActionType.PollDefinitionRunComplete:
      return false;
  }
  return state;
}

// Don't know if we need the space in a selected definition
export function spaceReducer(
  state = initialSelectedDefinitionState.space,
  action: Actions
): SelectedDefinitionState['space'] {
  return state;
}

export const SelectedDefinitionReducer: ReducerTree<AppState, SelectedDefinitionState, Actions> = {
  definition: definitionReducer,
  query: queryReducer,
  reportRun: reportRunReducer,
  queryRun: queryRunReducer,
  model: modelReducer,
  chartModel: chartModelReducer,
  pollingDefinitionRun: pollingReportRunReducer,
  space: spaceReducer,
};
